import React, { useState } from 'react';
import './TrackingPage.css';
import { useRugbyTracker } from '../../context/RugbyTrackerContext';
import { useTheme } from '../../hooks/useTheme';
import { getFieldPosition, checkDuplicateEvent } from '../../utils/eventUtils';
import { getFieldZone } from '../../constants/rugbyConstants';
import { resetTagsAndComment } from '../../utils/tagUtils';

// Import components
import UnifiedEventInput from './components/UnifiedEventInput';
import EventsList from './components/EventsList';
import FieldControls from './components/FieldControls';
import RugbyField from './components/RugbyField';

// Import CSV export utility
import { exportEventsToCsv } from '../../utils/csvExport';

/**
 * Main TrackingPage component, refactored to use Context API with useReducer
 * and theme awareness with unified event input component
 */
export const TrackingPage = ({
  showToast,
  showSuccessToast,
  showErrorToast,
  showWarningToast
}) => {
  // Get state and actions from context
  const { state, actions } = useRugbyTracker();
  
  // Get current theme
  const { theme } = useTheme();
  
  // Extract necessary state
  const {
    homeTeamName,
    awayTeamName,
    homeTeamScore,
    awayTeamScore,
    seconds,
    isRunning,
    formattedTime,
    matchPeriod,
    events,
    activeSanctions,
    activeEventType,
    activeTeam,
    comment,
    teamDirection
  } = state;

  // Local state for managing selected tags
  const [selectedTags, setSelectedTags] = useState([]);

  // Function to handle period changes with confirmation dialogs
  const handlePeriodChange = (newPeriod, skipConfirmation = false) => {
    // If trying to change to the current period, do nothing
    if (newPeriod === matchPeriod) return;
    
    // If skipConfirmation is true, change period without confirmation
    if (skipConfirmation) {
      actions.setMatchPeriod(newPeriod);
      return;
    }
    
    // Function to reverse team direction
    const reverseDirection = () => {
      const newDirection = teamDirection === 'left-to-right' ? 'right-to-left' : 'left-to-right';
      actions.setTeamDirection(newDirection);
      showToast(`Playing direction automatically changed to ${newDirection === 'left-to-right' ? 'Left to Right' : 'Right to Left'}`, 'info');
    };
    
    if (newPeriod === 'Second Half') {
      // Confirm transition to second half
      if (window.confirm('Change to Second Half? This will set the timer to 40:00, pause it, and reverse the playing direction.')) {
        // Set timer to 40:00 (40 minutes in seconds = 2400)
        actions.setSeconds(2400);
        actions.pauseTimer();
        actions.setMatchPeriod(newPeriod);
        // Reverse team direction
        reverseDirection();
        showToast('Changed to Second Half. Timer set to 40:00.', 'info');
      }
    } else if (newPeriod === 'Overtime') {
      // Confirm transition to overtime
      if (window.confirm('Change to Overtime? This will set the timer to 80:00, pause it, and reverse the playing direction.')) {
        // Set timer to 80:00 (80 minutes in seconds = 4800)
        actions.setSeconds(4800);
        actions.pauseTimer();
        actions.setMatchPeriod(newPeriod);
        // Reverse team direction
        reverseDirection();
        showToast('Changed to Overtime. Timer set to 80:00.', 'info');
      }
    } else {
      // For first half, just ask for confirmation without changing timer
      if (window.confirm(`Change to ${newPeriod}? This will also reverse the playing direction.`)) {
        actions.setMatchPeriod(newPeriod);
        // Reverse team direction
        reverseDirection();
        showToast(`Changed to ${newPeriod}`, 'info');
      }
    }
  };

  // Function to handle undoing the last recorded event
  const handleUndoLastEvent = () => {
    if (events.length > 0) {
      const lastEvent = events[events.length - 1];
      if (window.confirm(`Undo last event: "${lastEvent.eventType}" at ${lastEvent.timestamp}?`)) {
        actions.deleteEvent(lastEvent.id);
      }
    }
  };

  // Function to handle reset all data
  const handleResetAll = () => {
    if (window.confirm('Are you sure you want to reset all event data and the timer? This cannot be undone.')) {
      actions.resetAll();
      setSelectedTags([]); // Reset tags too
      showSuccessToast('All data has been reset');
    }
  };

  // Function to export events data as CSV using the utility function
  const handleExportToCSV = () => {
    exportEventsToCsv(events, homeTeamName, awayTeamName, showToast);
  };
  
  // Function to save match to database
  const handleSaveMatch = async () => {
    try {
      const result = await actions.saveMatch();
      
      // Check if result is a special 'local-storage-only' identifier
      if (result === 'local-storage-only') {
        showWarningToast('Match saved locally. Sign in to save to the cloud.');
      } else {
        showSuccessToast('Match saved successfully');
      }
    } catch (error) {
      // Handle different types of errors
      if (error.message && error.message.includes('User not authenticated')) {
        // Authentication errors
        showWarningToast('Please log in to save your match to the database');
      } else if (error.message && error.message.includes('NetworkError')) {
        // Network errors
        showErrorToast('Network error. Match saved locally only.');
      } else {
        // General errors
        console.error('Error saving match:', error);
        showErrorToast('Failed to save match: ' + (error.message || 'Unknown error'));
      }
    }
  };

  // Direct field click handler
  const handleDirectFieldClick = (e) => {
    // Use SVG-specific position if available, otherwise fall back to the original method
    const position = e.svgPosition || getFieldPosition(e, e.currentTarget);

    // For direct capture, we immediately save the event if team and eventType are selected
    if (!activeTeam || !activeEventType) {
      showErrorToast('Please select team and event type before recording an event');
      return;
    }

    // Check for duplicate clicks (same position within a short time)
    const isDuplicate = checkDuplicateEvent(events, position, seconds, activeEventType, activeTeam);
    if (isDuplicate) {
      showWarningToast('Duplicate event detected. Ignoring click.');
      return;
    }

    // Create new event
    const newEvent = {
      id: Date.now(),
      position,
      timestamp: formattedTime,
      seconds,
      period: matchPeriod,
      eventType: activeEventType,
      team: activeTeam,
      comment: comment.trim() || null,
      zone: getFieldZone(position.x, matchPeriod, teamDirection)
    };

    // Add the event using context action
    actions.addEvent(newEvent);
    
    // Show notification for card events
    if (activeEventType === 'Yellow Card' || activeEventType === 'Red Card') {
      const duration = activeEventType === 'Yellow Card' ? '10 minutes' : '20 minutes';
      const teamName = activeTeam === 'Home' ? homeTeamName : awayTeamName;
      showToast(`${teamName} received a ${activeEventType.toLowerCase()} (${duration})`, 'warning');
    }

    showSuccessToast('Event recorded');

    // Reset tags and comment after recording the event
    resetTagsAndComment(setSelectedTags, actions.setComment);
  };

  // Add theme-aware class to tracking page
  const trackingPageClass = `tracking-page ${theme === 'high-contrast' ? 'high-contrast' : ''}`;

  return (
    <div className={trackingPageClass}>
      {/* Main Content */}
      <div className="tracking-content">
        {/* Left Column - Controls */}
        <div className="tracking-controls">
          <UnifiedEventInput
            eventType={activeEventType}
            setEventType={actions.setEventType}
            team={activeTeam}
            setTeam={actions.setTeam}
            homeTeamName={homeTeamName}
            awayTeamName={awayTeamName}
            setHomeTeamName={actions.setHomeTeamName}
            setAwayTeamName={actions.setAwayTeamName}
            teamDirection={teamDirection}
            setTeamDirection={actions.setTeamDirection}
            comment={comment}
            setComment={actions.setComment}
            EVENT_TYPES={state.EVENT_TYPES}
            selectedTags={selectedTags}
            setSelectedTags={setSelectedTags}
            showToast={showToast}
          />
          
          <EventsList 
            events={events}
            homeTeamName={homeTeamName}
            awayTeamName={awayTeamName}
            editEvent={actions.editEvent}
            deleteEvent={actions.deleteEvent}
            handleUndoLastEvent={handleUndoLastEvent}
            showToast={showToast}
          />
        </div>

        {/* Right Column - Rugby Field */}
        <div className="field-container">
          <FieldControls 
            formattedTime={formattedTime}
            isRunning={isRunning}
            startTimer={actions.startTimer}
            pauseTimer={actions.pauseTimer}
            resetTimer={actions.resetTimer}
            setTime={actions.setSeconds}
            homeTeamName={homeTeamName}
            awayTeamName={awayTeamName}
            homeTeamScore={homeTeamScore}
            awayTeamScore={awayTeamScore}
            matchPeriod={matchPeriod}
            handlePeriodChange={handlePeriodChange}
            handleResetAll={handleResetAll}
            exportToCSV={handleExportToCSV}
            saveMatch={handleSaveMatch}
            events={events}
            showToast={showToast}
            // Sanctions props
            activeSanctions={activeSanctions}
            getRemainingTime={actions.getRemainingTime}
            getFormattedRemainingTime={actions.getFormattedRemainingTime}
            isInWarningState={actions.isInWarningState}
          />

          <RugbyField
            events={events}
            homeTeamName={homeTeamName}
            awayTeamName={awayTeamName}
            handleDirectFieldClick={handleDirectFieldClick}
            teamDirection={teamDirection}
          />
        </div>
      </div>
    </div>
  );
};

export default TrackingPage;